import { useContext, useEffect, useState } from 'react';
import { AppContext, isAgentHawkEnabledForStageRegion } from '../../../utils/appContext';

const HEALTH_ENDPOINT = '/api/agentHawk/health';

/**
 * Shared across every caller so the health check runs once per page load
 * instead of once per component that renders an Agent Hawk entry point.
 */
let healthPromise: Promise<boolean> | null = null;

/**
 * The BFF resolves AgentHawk config for its own region at startup (Merlin
 * account plus the runtime/memory SSM parameters), so this endpoint reports
 * `enabled` only where Agent Hawk is actually deployed.
 */
const fetchHealth = (): Promise<boolean> => {
  if (!healthPromise) {
    healthPromise = fetch(HEALTH_ENDPOINT, { credentials: 'include' })
      .then(response => response.json())
      .then(data => !!data.enabled)
      .catch(() => false);
  }
  return healthPromise;
};

export interface AgentHawkAvailability {
  /** True while the backend health check is still in flight. */
  loading: boolean;
  /** True once Agent Hawk is confirmed available for this stage, region and BFF. */
  available: boolean;
  /** True when the current stage/region is allowlisted for Agent Hawk. */
  enabledForStageRegion: boolean;
}

/**
 * Resolves whether Agent Hawk entry points should render: the stage/region must
 * be allowlisted (see `AGENT_HAWK_PROD_PUBLIC_REGIONS`) and the BFF must report
 * Agent Hawk as enabled.
 */
export const useAgentHawkAvailability = (): AgentHawkAvailability => {
  const appContext = useContext(AppContext);
  const enabledForStageRegion = isAgentHawkEnabledForStageRegion(appContext.stage, appContext.region);
  const [loading, setLoading] = useState(enabledForStageRegion);
  const [healthy, setHealthy] = useState(false);

  useEffect(() => {
    if (!enabledForStageRegion) return;

    let cancelled = false;
    fetchHealth().then(enabled => {
      if (cancelled) return;
      setHealthy(enabled);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabledForStageRegion]);

  return { loading, available: enabledForStageRegion && healthy, enabledForStageRegion };
};
